import React, { useCallback, useEffect, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { buildFromFileName, signRelease } from '../../utils/tallyCoreSign';
import './TallyTwoAdmin.css';

/**
 * RiskLo Tally 2 (Full Admin testing). v1 (1.4.9) is untouched; this is the
 * loader + signed core version (addons/risklo-tally-v2/README.md).
 *
 *   TallyTwoDownloads   the loader packages and the Windows core build kit
 *   TallyTwoReleases    publish a signed core, promote canary → stable,
 *                       canary machines, who runs what
 */

const FILES = [
  { id: 'self-use', label: 'Self-use loader', note: 'Import in NinjaTrader instead of v1 (replaces it; import the v1 zip again to go back).' },
  { id: 'csa', label: 'CSA alert-only loader', note: 'Only accepts CSA-signed cores, which can never close, change or cancel a trade.' },
  { id: 'kit', label: 'Core build kit (Windows)', note: 'Builds both core DLLs with build-core.ps1. See README-WINDOWS.txt inside.' },
];

async function download(file, knownName) {
  const res = await fetch(API_ENDPOINTS.tradeWatcherAddonV2Download(file), { headers: getBearerAuthHeaders() });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Couldn’t download it');
  }
  const blob = await res.blob();
  // The file name header isn't always readable across origins; the info endpoint has it.
  const name = /filename="?([^"]+)"?/.exec(res.headers.get('Content-Disposition') || '')?.[1] || knownName || `RiskLoTally2_${file}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function TallyTwoDownloads() {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [version, setVersion] = useState('');
  const [names, setNames] = useState({});
  useEffect(() => {
    fetch(API_ENDPOINTS.tradeWatcherAddonV2Info, { headers: getBearerAuthHeaders() })
      .then((r) => r.json())
      .then((d) => { if (d.success) { setVersion(d.version); setNames(d.files || {}); } })
      .catch(() => {});
  }, []);
  const get = async (id) => {
    setBusy(id);
    setError('');
    try { await download(id, names[id]); } catch (e) { setError(e.message); } finally { setBusy(''); }
  };
  return (
    <div className="tw-other-downloads t2-downloads">
      <span className="vd-control-label">RiskLo Tally 2{version ? ` v${version}` : ''} (admin testing)</span>
      <ul>
        {FILES.map((f) => (
          <li key={f.id}>
            <button type="button" className="tw-link t2-download" onClick={() => get(f.id)} disabled={Boolean(busy)}>
              {busy === f.id ? 'Downloading…' : f.label}
            </button>
            <span className="tw-muted"> {f.note}</span>
          </li>
        ))}
      </ul>
      {error ? <p className="t2-error" role="alert">{error}</p> : null}
      <p className="tw-muted t2-small">
        Updates itself from RiskLo: the loader downloads the signed core, so after this one import nobody installs anything again.
        Publish cores under Settings → RiskLo Tally 2 releases.
      </p>
    </div>
  );
}

const authJson = () => ({ 'Content-Type': 'application/json', ...getBearerAuthHeaders() });
const shortSha = (s) => (s ? s.slice(0, 10) : '—');
const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function readFile(file, as) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error(`Couldn’t read ${file.name}`));
    if (as === 'text') r.readAsText(file);
    else r.readAsDataURL(file);
  });
}

export function TallyTwoReleases() {
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [dll, setDll] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [version, setVersion] = useState('2.0.0');
  const [mode, setMode] = useState('install');
  const [channel, setChannel] = useState('canary');
  const [email, setEmail] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.tallyCoreAdminState, { headers: getBearerAuthHeaders() });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t load releases');
      setState(d);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    setNote('');
    try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setBusy(''); }
  };

  // Signed right here in the browser with the key file you pick (it's never
  // uploaded), or with a .manifest.json already signed by release.js.
  const publish = () => run('publish', async () => {
    if (!dll) throw new Error('Choose the core DLL (out\\RiskLoTallyCore_SelfUse.dll or _CSA.dll).');
    const build = buildFromFileName(dll.name);
    if (!build) throw new Error('The DLL should be named RiskLoTallyCore_SelfUse.dll or RiskLoTallyCore_CSA.dll.');
    if (!manifest && !keyFile) throw new Error(`Choose your signing key file (${build}-1.pem).`);
    if (!/^\d+\.\d+\.\d+$/.test(version)) throw new Error('Version should look like 2.0.1.');
    const dataUrl = await readFile(dll, 'data');
    let text;
    if (manifest) {
      text = await readFile(manifest, 'text');
    } else {
      const bytes = await dll.arrayBuffer();
      const signed = await signRelease({
        dllBytes: bytes,
        keyPem: await readFile(keyFile, 'text'),
        build,
        version,
        mandatory: mode !== 'offer',
        disabled: mode === 'disable',
        stopNow: mode === 'stop',
      });
      text = signed.manifestText;
    }
    const res = await fetch(API_ENDPOINTS.tallyCoreAdminPublish, {
      method: 'POST',
      headers: authJson(),
      body: JSON.stringify({ manifest: text, dllBase64: String(dataUrl).split(',')[1] || '', channel }),
    });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t publish');
    setNote(`Published ${d.release.build} ${d.release.version} (seq ${d.release.seq}) on ${d.release.channel}: ${d.release.flags}.`);
    setDll(null);
    setManifest(null);
  });

  const promote = (build) => run(`promote-${build}`, async () => {
    if (!window.confirm(`Promote the ${build} canary release to every ${build} machine (stable)?`)) return;
    const res = await fetch(API_ENDPOINTS.tallyCoreAdminPromote, { method: 'POST', headers: authJson(), body: JSON.stringify({ build }) });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t promote');
    setNote(`${build} ${d.promoted.version} is now on stable.`);
  });

  const canary = (address, on) => run(`canary-${address}`, async () => {
    const res = await fetch(API_ENDPOINTS.tallyCoreAdminCanary, { method: 'PUT', headers: authJson(), body: JSON.stringify({ email: address, on }) });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.error || 'Couldn’t update');
    if (on) setEmail('');
  });

  const channelRow = (build, ch) => (state?.channels || []).find((c) => c.build === build && c.channel === ch);

  return (
    <section className="vd-panel vd-ml-widget t2">
      <div className="vd-ml-widget-header tw-section-head">
        <div>
          <h3>RiskLo Tally 2 releases <span className="t2-badge">Full Admin</span></h3>
          <p className="tw-muted tw-section-sub">
            Signed core DLLs the RiskLo Tally 2 loader downloads. Canary machines get the canary release; everyone on v2 gets stable.
            Sign on your own machine with <code>release.js</code>; RiskLo never holds the signing keys.
          </p>
        </div>
      </div>
      {error ? <p className="t2-error" role="alert">{error}</p> : null}
      {note ? <p className="t2-note" role="status">{note}</p> : null}
      {!state && !error ? <p className="tw-muted">Loading…</p> : null}

      {state ? (
        <>
          {!state.keys?.length ? <p className="t2-error">No public keys on the server (server/assets/tally-core/keys). Run release.js keys and deploy.</p> : null}
          <div className="t2-builds">
            {['selfuse', 'csa'].map((build) => (
              <div key={build} className="t2-build">
                <h4>{build === 'selfuse' ? 'Self-use' : 'CSA alert-only'}</h4>
                {['canary', 'stable'].map((ch) => {
                  const r = channelRow(build, ch);
                  return (
                    <div key={ch} className="t2-channel">
                      <span className={`t2-pill is-${ch}`}>{ch}</span>
                      {r ? (
                        <span>
                          <strong>{r.version}</strong> · seq {r.seq} · {shortSha(r.sha256)} · {r.flags}
                          <span className="tw-muted"> · {when(r.publishedAt)}</span>
                        </span>
                      ) : <span className="tw-muted">Nothing published</span>}
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="vd-btn vd-client-resync-btn"
                  onClick={() => promote(build)}
                  disabled={!channelRow(build, 'canary') || busy === `promote-${build}`}
                >
                  Promote canary → stable
                </button>
              </div>
            ))}
          </div>

          <h4 className="t2-h">Publish a core</h4>
          <div className="t2-publish">
            <label>1. Core DLL <input type="file" accept=".dll" onChange={(e) => setDll(e.target.files?.[0] || null)} /></label>
            <label>2. Your signing key <input type="file" accept=".pem" onChange={(e) => setKeyFile(e.target.files?.[0] || null)} /></label>
            <label>Version <input className="t2-version" value={version} onChange={(e) => setVersion(e.target.value.trim())} /></label>
            <label>What it does
              <select value={mode} onChange={(e) => setMode(e.target.value)}>
                <option value="install">Install (once every position is flat)</option>
                <option value="offer">Ask each trader first</option>
                <option value="disable">Turn the add-on off (when flat)</option>
                <option value="stop">Stop it now (emergency)</option>
              </select>
            </label>
            <label>Channel
              <select value={channel} onChange={(e) => setChannel(e.target.value)}>
                <option value="canary">Canary (test machines)</option>
                <option value="stable">Stable (every v2 machine)</option>
              </select>
            </label>
            <button type="button" className="vd-btn" onClick={publish} disabled={busy === 'publish'}>{busy === 'publish' ? 'Publishing…' : 'Publish'}</button>
          </div>
          <p className="tw-muted t2-small">
            The key file (<code>selfuse-1.pem</code> / <code>csa-1.pem</code>) is read here to sign and never leaves this computer.
            Roll back by publishing the older DLL again. Stop switch: publish the current DLL with “Stop it now”.
          </p>
          <details className="t2-small">
            <summary>Already signed with release.js?</summary>
            <label>Signed manifest <input type="file" accept=".json,application/json" onChange={(e) => setManifest(e.target.files?.[0] || null)} /></label>
          </details>

          <h4 className="t2-h">Canary machines</h4>
          <div className="t2-canary">
            {(state.canary || []).length ? (
              <ul>
                {state.canary.map((c) => (
                  <li key={c.feed_id}>
                    {c.user_email}
                    <button type="button" className="tw-link" onClick={() => canary(c.user_email, false)} disabled={Boolean(busy)}>Remove</button>
                  </li>
                ))}
              </ul>
            ) : <p className="tw-muted t2-small">None yet. Add yourself to test a release before promoting it.</p>}
            <div className="t2-row">
              <input type="email" placeholder="email of a Trade Watcher feed" value={email} onChange={(e) => setEmail(e.target.value)} />
              <button type="button" className="vd-btn vd-client-resync-btn" onClick={() => canary(email, true)} disabled={!email || Boolean(busy)}>Add to canary</button>
            </div>
          </div>

          <h4 className="t2-h">Machines on v2</h4>
          {(state.checkins || []).length ? (
            <div className="tw-table-wrap">
              <table className="tw-table t2-table">
                <thead><tr><th>Feed</th><th>Build</th><th>Loader</th><th>Core</th><th>Channel</th><th>Last check</th></tr></thead>
                <tbody>
                  {state.checkins.map((k) => (
                    <tr key={`${k.feed_id}-${k.build}`}>
                      <td>{k.user_email}</td><td>{k.build}</td><td>{k.loader_version || '—'}</td>
                      <td>{k.core_version || 'not running'}</td><td>{k.channel || '—'}</td><td>{when(k.checked_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="tw-muted t2-small">No machine has checked in yet.</p>}

          {(state.history || []).length ? (
            <>
              <h4 className="t2-h">History</h4>
              <ul className="t2-history">
                {state.history.map((h, i) => (
                  <li key={`${h.at}-${i}`}>
                    <span className="tw-muted">{when(h.at)}</span> {h.action} {h.build} {h.version} (seq {h.seq}) → {h.channel} · {h.flags}
                    <span className="tw-muted"> · {h.by_email || '—'}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
